import { ArchiveIcon, ArchiveX, LoaderIcon } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useCallback, useMemo } from "react";
import { type ScopedThreadRef } from "@t3tools/contracts";
import { scopeThreadRef } from "@t3tools/client-runtime";
import { DEFAULT_CLIENT_SETTINGS } from "@t3tools/contracts/settings";
import {
  APP_BASE_NAME,
  APP_VERSION,
  HOSTED_APP_CHANNEL,
  HOSTED_APP_CHANNEL_LABEL,
} from "../../branding";
import { buildHostedChannelSelectionUrl, type HostedAppChannel } from "../../hostedPairing";
import { useTheme } from "../../hooks/useTheme";
import {
  DEFAULT_LOCALE,
  isLocale,
  LOCALE_LABELS,
  LOCALES,
  setLocale,
  type Locale,
} from "../../i18n";
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { useThreadActions } from "../../hooks/useThreadActions";
import { ensureLocalApi, readLocalApi } from "../../localApi";
import { useShallow } from "zustand/react/shallow";
import { selectProjectsAcrossEnvironments, useStore } from "../../store";
import { useArchivedThreadSnapshots } from "../../lib/archivedThreadsState";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { stackedThreadToast, toastManager } from "../ui/toast";
import {
  SettingResetButton,
  SettingsPageContainer,
  SettingsRow,
  SettingsSection,
} from "./settingsLayout";
import { ProjectFavicon } from "../ProjectFavicon";
import { InspectSettingsSection } from "./InspectSettings";

const THEME_OPTIONS = [
  {
    value: "system",
    label: msg`System`,
  },
  {
    value: "light",
    label: msg`Light`,
  },
  {
    value: "dark",
    label: msg`Dark`,
  },
] as const;

const TIMESTAMP_FORMAT_LABELS = {
  locale: msg`System default`,
  "12-hour": msg`12-hour`,
  "24-hour": msg`24-hour`,
} as const;

function AboutVersionTitle() {
  return (
    <span className="inline-flex items-center gap-2">
      <span>
        <Trans>Version</Trans>
      </span>
      <code className="text-[11px] font-medium text-muted-foreground">{APP_VERSION}</code>
    </span>
  );
}

function AboutVersionSection() {
  const { t } = useLingui();

  return (
    <>
      <SettingsRow
        title={<AboutVersionTitle />}
        description={t`Current version of the application.`}
      />
      {HOSTED_APP_CHANNEL ? (
        <SettingsRow
          title={t`Update track`}
          description={t`Switches the hosted app release channel.`}
          control={
            <Select
              value={HOSTED_APP_CHANNEL}
              onValueChange={(value) => {
                if (value === HOSTED_APP_CHANNEL) return;
                window.location.assign(
                  buildHostedChannelSelectionUrl({ channel: value as HostedAppChannel }),
                );
              }}
            >
              <SelectTrigger className="w-full sm:w-40" aria-label={t`Update track`}>
                <SelectValue>{HOSTED_APP_CHANNEL_LABEL}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="latest">
                  <Trans>Latest</Trans>
                </SelectItem>
                <SelectItem hideIndicator value="nightly">
                  <Trans>Nightly</Trans>
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />
      ) : null}
    </>
  );
}

export function useSettingsRestore(onRestored?: () => void) {
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useLingui();
  const locale = i18n.locale as Locale;
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();

  const changedSettingLabels = useMemo(
    () => [
      ...(theme !== "system" ? [t`Theme`] : []),
      ...(locale !== DEFAULT_LOCALE ? [t`Language`] : []),
      ...(settings.timestampFormat !== DEFAULT_CLIENT_SETTINGS.timestampFormat
        ? [t`Time format`]
        : []),
      ...(settings.sidebarThreadPreviewCount !== DEFAULT_CLIENT_SETTINGS.sidebarThreadPreviewCount
        ? [t`Visible threads`]
        : []),
      ...(settings.autoOpenPlanSidebar !== DEFAULT_CLIENT_SETTINGS.autoOpenPlanSidebar
        ? [t`Auto-open task panel`]
        : []),
      ...(settings.confirmThreadArchive !== DEFAULT_CLIENT_SETTINGS.confirmThreadArchive
        ? [t`Archive confirmation`]
        : []),
      ...(settings.confirmThreadDelete !== DEFAULT_CLIENT_SETTINGS.confirmThreadDelete
        ? [t`Delete confirmation`]
        : []),
    ],
    [
      locale,
      t,
      settings.autoOpenPlanSidebar,
      settings.confirmThreadArchive,
      settings.confirmThreadDelete,
      settings.sidebarThreadPreviewCount,
      settings.timestampFormat,
      theme,
    ],
  );

  const restoreDefaults = useCallback(async () => {
    if (changedSettingLabels.length === 0) return;
    const api = readLocalApi();
    const changedList = changedSettingLabels.join(", ");
    const confirmed = await (api ?? ensureLocalApi()).dialogs.confirm(
      [t`Restore default settings?`, t`This will reset: ${changedList}.`].join("\n"),
    );
    if (!confirmed) return;

    setTheme("system");
    setLocale(DEFAULT_LOCALE);
    updateSettings({
      timestampFormat: DEFAULT_CLIENT_SETTINGS.timestampFormat,
      sidebarThreadPreviewCount: DEFAULT_CLIENT_SETTINGS.sidebarThreadPreviewCount,
      autoOpenPlanSidebar: DEFAULT_CLIENT_SETTINGS.autoOpenPlanSidebar,
      confirmThreadArchive: DEFAULT_CLIENT_SETTINGS.confirmThreadArchive,
      confirmThreadDelete: DEFAULT_CLIENT_SETTINGS.confirmThreadDelete,
    });
    onRestored?.();
  }, [changedSettingLabels, onRestored, setTheme, t, updateSettings]);

  return {
    changedSettingLabels,
    restoreDefaults,
  };
}

export function GeneralSettingsPanel() {
  const { theme, setTheme } = useTheme();
  const { t, i18n } = useLingui();
  const locale = i18n.locale as Locale;
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();

  return (
    <SettingsPageContainer>
      <SettingsSection title={t`General`}>
        <SettingsRow
          title={t`Theme`}
          description={t`Choose how ${APP_BASE_NAME} looks across the app.`}
          resetAction={
            theme !== "system" ? (
              <SettingResetButton label={t`theme`} onClick={() => setTheme("system")} />
            ) : null
          }
          control={
            <Select
              value={theme}
              onValueChange={(value) => {
                if (value === "system" || value === "light" || value === "dark") {
                  setTheme(value);
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-40" aria-label={t`Theme preference`}>
                <SelectValue>
                  {i18n._(
                    THEME_OPTIONS.find((option) => option.value === theme)?.label ?? msg`System`,
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {THEME_OPTIONS.map((option) => (
                  <SelectItem hideIndicator key={option.value} value={option.value}>
                    {i18n._(option.label)}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          title={t`Language`}
          description={t`Language of the app interface.`}
          resetAction={
            locale !== DEFAULT_LOCALE ? (
              <SettingResetButton label={t`language`} onClick={() => setLocale(DEFAULT_LOCALE)} />
            ) : null
          }
          control={
            <Select
              value={locale}
              onValueChange={(value) => {
                if (isLocale(value)) {
                  setLocale(value);
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-40" aria-label={t`Language preference`}>
                <SelectValue>{LOCALE_LABELS[locale]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {LOCALES.map((option) => (
                  <SelectItem hideIndicator key={option} value={option}>
                    {LOCALE_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          title={t`Time format`}
          description={t`System default follows your browser or OS clock preference.`}
          resetAction={
            settings.timestampFormat !== DEFAULT_CLIENT_SETTINGS.timestampFormat ? (
              <SettingResetButton
                label={t`time format`}
                onClick={() =>
                  updateSettings({
                    timestampFormat: DEFAULT_CLIENT_SETTINGS.timestampFormat,
                  })
                }
              />
            ) : null
          }
          control={
            <Select
              value={settings.timestampFormat}
              onValueChange={(value) => {
                if (value === "locale" || value === "12-hour" || value === "24-hour") {
                  updateSettings({ timestampFormat: value });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-40" aria-label={t`Timestamp format`}>
                <SelectValue>
                  {i18n._(TIMESTAMP_FORMAT_LABELS[settings.timestampFormat])}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="locale">
                  {i18n._(TIMESTAMP_FORMAT_LABELS.locale)}
                </SelectItem>
                <SelectItem hideIndicator value="12-hour">
                  {i18n._(TIMESTAMP_FORMAT_LABELS["12-hour"])}
                </SelectItem>
                <SelectItem hideIndicator value="24-hour">
                  {i18n._(TIMESTAMP_FORMAT_LABELS["24-hour"])}
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          title={t`Auto-open task panel`}
          description={t`Open the right-side plan and task panel automatically when steps appear.`}
          resetAction={
            settings.autoOpenPlanSidebar !== DEFAULT_CLIENT_SETTINGS.autoOpenPlanSidebar ? (
              <SettingResetButton
                label={t`auto-open task panel`}
                onClick={() =>
                  updateSettings({
                    autoOpenPlanSidebar: DEFAULT_CLIENT_SETTINGS.autoOpenPlanSidebar,
                  })
                }
              />
            ) : null
          }
          control={
            <Switch
              checked={settings.autoOpenPlanSidebar}
              onCheckedChange={(checked) =>
                updateSettings({ autoOpenPlanSidebar: Boolean(checked) })
              }
              aria-label={t`Open the task panel automatically`}
            />
          }
        />

        <SettingsRow
          title={t`Archive confirmation`}
          description={t`Require a second click on the inline archive action before a thread is archived.`}
          resetAction={
            settings.confirmThreadArchive !== DEFAULT_CLIENT_SETTINGS.confirmThreadArchive ? (
              <SettingResetButton
                label={t`archive confirmation`}
                onClick={() =>
                  updateSettings({
                    confirmThreadArchive: DEFAULT_CLIENT_SETTINGS.confirmThreadArchive,
                  })
                }
              />
            ) : null
          }
          control={
            <Switch
              checked={settings.confirmThreadArchive}
              onCheckedChange={(checked) =>
                updateSettings({ confirmThreadArchive: Boolean(checked) })
              }
              aria-label={t`Confirm thread archiving`}
            />
          }
        />

        <SettingsRow
          title={t`Delete confirmation`}
          description={t`Ask before deleting a thread and its chat history.`}
          resetAction={
            settings.confirmThreadDelete !== DEFAULT_CLIENT_SETTINGS.confirmThreadDelete ? (
              <SettingResetButton
                label={t`delete confirmation`}
                onClick={() =>
                  updateSettings({
                    confirmThreadDelete: DEFAULT_CLIENT_SETTINGS.confirmThreadDelete,
                  })
                }
              />
            ) : null
          }
          control={
            <Switch
              checked={settings.confirmThreadDelete}
              onCheckedChange={(checked) =>
                updateSettings({ confirmThreadDelete: Boolean(checked) })
              }
              aria-label={t`Confirm thread deletion`}
            />
          }
        />
      </SettingsSection>

      <InspectSettingsSection />

      <SettingsSection title={t`About`}>
        <AboutVersionSection />
      </SettingsSection>
    </SettingsPageContainer>
  );
}

export function ArchivedThreadsPanel() {
  const { t } = useLingui();
  const projects = useStore(useShallow(selectProjectsAcrossEnvironments));
  const { unarchiveThread, confirmAndDeleteThread } = useThreadActions();
  const environmentIds = useMemo(
    () => [...new Set(projects.map((project) => project.environmentId))],
    [projects],
  );
  const {
    snapshots: archivedSnapshots,
    error: archiveError,
    isLoading: isLoadingArchive,
    refresh: refreshArchivedThreads,
  } = useArchivedThreadSnapshots(environmentIds);

  const archivedGroups = useMemo(() => {
    const projectsByEnvironmentAndId = new Map(
      archivedSnapshots.flatMap(({ environmentId, snapshot }) =>
        snapshot.projects.map(
          (project) =>
            [
              `${environmentId}:${project.id}`,
              {
                id: project.id,
                environmentId,
                name: project.title,
                cwd: project.workspaceRoot,
              },
            ] as const,
        ),
      ),
    );
    const threads = archivedSnapshots.flatMap(({ environmentId, snapshot }) =>
      snapshot.threads.map((thread) => ({
        ...thread,
        environmentId,
      })),
    );

    const archivedProjects = Array.from(projectsByEnvironmentAndId.values());
    const groups: Array<{
      readonly project: (typeof archivedProjects)[number];
      readonly threads: Array<(typeof threads)[number]>;
    }> = [];
    for (const project of archivedProjects) {
      const projectThreads: Array<(typeof threads)[number]> = [];
      for (const thread of threads) {
        if (thread.projectId === project.id && thread.environmentId === project.environmentId) {
          projectThreads.push(thread);
        }
      }
      if (projectThreads.length > 0) {
        groups.push({
          project,
          threads: projectThreads.toSorted((left, right) => {
            const leftKey = left.archivedAt ?? left.createdAt;
            const rightKey = right.archivedAt ?? right.createdAt;
            return rightKey.localeCompare(leftKey) || right.id.localeCompare(left.id);
          }),
        });
      }
    }
    return groups;
  }, [archivedSnapshots]);

  const handleArchivedThreadContextMenu = useCallback(
    async (threadRef: ScopedThreadRef, position: { x: number; y: number }) => {
      const api = readLocalApi();
      if (!api) return;
      const clicked = await api.contextMenu.show(
        [
          { id: "unarchive", label: t`Unarchive` },
          { id: "delete", label: t`Delete`, destructive: true },
        ],
        position,
      );

      if (clicked === "unarchive") {
        try {
          await unarchiveThread(threadRef);
          refreshArchivedThreads();
        } catch (error) {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t`Failed to unarchive thread`,
              description: error instanceof Error ? error.message : t`An error occurred.`,
            }),
          );
        }
        return;
      }

      if (clicked === "delete") {
        await confirmAndDeleteThread(threadRef);
        refreshArchivedThreads();
      }
    },
    [confirmAndDeleteThread, refreshArchivedThreads, t, unarchiveThread],
  );

  return (
    <SettingsPageContainer>
      {archivedGroups.length === 0 ? (
        <SettingsSection title={t`Archived threads`}>
          <SettingsRow
            title={
              <span className="inline-flex items-center gap-2">
                {isLoadingArchive ? (
                  <LoaderIcon className="size-3.5 animate-spin text-muted-foreground" />
                ) : (
                  <ArchiveIcon className="size-3.5 text-muted-foreground" />
                )}
                {isLoadingArchive
                  ? t`Loading archived threads`
                  : archiveError
                    ? t`Could not load archived threads`
                    : t`No archived threads`}
              </span>
            }
            description={
              isLoadingArchive
                ? t`Checking connected environments.`
                : (archiveError ?? t`Archived threads will appear here.`)
            }
          />
        </SettingsSection>
      ) : (
        archivedGroups.map(({ project, threads: projectThreads }) => (
          <SettingsSection
            key={project.id}
            title={project.name}
            icon={<ProjectFavicon environmentId={project.environmentId} cwd={project.cwd} />}
          >
            {projectThreads.map((thread) => (
              <SettingsRow
                key={thread.id}
                onContextMenu={(event) => {
                  event.preventDefault();
                  void handleArchivedThreadContextMenu(
                    scopeThreadRef(thread.environmentId, thread.id),
                    {
                      x: event.clientX,
                      y: event.clientY,
                    },
                  );
                }}
                title={thread.title}
                description={
                  <Trans>
                    Archived {formatRelativeTimeLabel(thread.archivedAt ?? thread.createdAt)}
                    {" \u00b7 Created "}
                    {formatRelativeTimeLabel(thread.createdAt)}
                  </Trans>
                }
                control={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 shrink-0 cursor-pointer gap-1.5 px-2.5"
                    onClick={() =>
                      void unarchiveThread(scopeThreadRef(thread.environmentId, thread.id))
                        .then(() => refreshArchivedThreads())
                        .catch((error) => {
                          toastManager.add(
                            stackedThreadToast({
                              type: "error",
                              title: t`Failed to unarchive thread`,
                              description:
                                error instanceof Error ? error.message : t`An error occurred.`,
                            }),
                          );
                        })
                    }
                  >
                    <ArchiveX className="size-3.5" />
                    <span>
                      <Trans>Unarchive</Trans>
                    </span>
                  </Button>
                }
              />
            ))}
          </SettingsSection>
        ))
      )}
    </SettingsPageContainer>
  );
}
